import React from "react";
import { Link } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import StateBadge from "@/components/StateBadge";
import { ArrowRight, ArrowUpRight } from "lucide-react";

const FEATURES = [
  ["Inventory management", "One console for every screen you operate. Slot pricing, availability, blackout windows."],
  ["Programmatic demand", "Route unsold inventory into programmatic — priced dynamically, filled automatically."],
  ["Direct campaigns", "Sell direct to brands with contracted CPM, terms, and playlists."],
  ["Proof of Play", "Verified delivery per second, per screen — signed and auditable."],
  ["Screen monitoring", "Health, uptime, temperature, playback anomalies — one dashboard."],
  ["Revenue & payouts", "Transparent settlement · monthly export · APIs for accounting."],
];

export default function MediaOwners() {
  return (
    <PublicShell>
      <section className="pt-28 md:pt-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-16">
          <div className="flex items-center gap-3">
            <StateBadge state="BETA" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">Media Owners</span>
          </div>
          <h1 className="mt-6 font-display font-black tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            Make every screen
            <br />
            <span className="text-white/30">more valuable.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/60 text-lg">
            Whether you run 3 screens or 3,000 — Mirrorloops brings direct
            demand, programmatic demand, and proof-of-play to every endpoint.
          </p>
          <div className="mt-10 flex gap-3 flex-wrap">
            <Link to="/register" data-testid="mo-cta-connect" className="px-6 py-3 bg-white text-black font-semibold hover:bg-white/90">
              Connect existing screens
            </Link>
            <Link to="/calculator" data-testid="mo-cta-calculator" className="px-6 py-3 border border-white/20 text-white hover:border-white">
              Run the calculator
            </Link>
            <Link to="/register" data-testid="mo-cta-install" className="px-6 py-3 text-white/60 hover:text-white">
              Install new screens →
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10" data-testid="mo-features">
            {FEATURES.map(([t, d]) => (
              <div key={t} className="bg-[#0A0908] p-8 min-h-[200px]">
                <div className="font-display font-extrabold text-xl mb-3">{t}</div>
                <div className="text-white/60 leading-relaxed text-sm">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10 bg-[#0d0c0b]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#E8D6A3] mb-4">Onboarding wizard</div>
            <h2 className="font-display font-black tracking-tighter leading-[0.95] text-4xl md:text-5xl">
              From ownership to live in 14 steps.
            </h2>
            <p className="mt-6 text-white/60 max-w-md">
              A guided wizard walks operators through venue → address → screen
              type → dimensions → resolution → orientation → hours → context → photos →
              ownership verification → screen verification → pricing → revenue share → node connection.
            </p>
          </div>
          <ol className="grid grid-cols-2 gap-1 font-mono text-[11px]">
            {["Venue","Address","Type","Dimensions","Resolution","Orientation","Hours","Context","Photos","Owner check","Screen check","Pricing","Rev share","Node"].map((s, i) => (
              <li key={s} className="p-3 border border-white/10 flex items-center gap-2 bg-white/[0.02]">
                <span className="text-[#E8D6A3]">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-white/70">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-center">
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl">
            Screens sell themselves. Better with proof.
          </h2>
          <Link to="/register" className="mt-8 inline-flex items-center gap-2 px-6 py-4 bg-white text-black font-bold">
            Get started <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}
